/**
 * Connectivity Dashboard
 * Dashboard completa per monitorare l'integrazione Communication Service.
 * 
 * Funzionalità:
 * - Health Check con versione API, tempo risposta, ultimo ping
 * - Ultimo webhook ricevuto e messaggio inviato
 * - Contatori: Sent, Delivered, Read, Failed, Received, Errored
 * - Log ultimi eventi
 * - Test invio messaggio
 * - Test webhook
 * - Verifica Health
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Wifi, WifiOff, RefreshCw, Send, Check, X, AlertCircle,
  Clock, MessageSquare, Activity, Settings, Server,
  CheckCircle2, XCircle, Zap, Database, Play, Eye,
  ArrowDown, ArrowUp, Bell, FileText, Webhook, Timer,
  Shield, Hash, Globe, ToggleLeft, ToggleRight
} from 'lucide-react';
import { API } from '../../lib/api';

export default function ConnectivityDiagnostics() {
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [verifyingHealth, setVerifyingHealth] = useState(false);
  
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [lastPing, setLastPing] = useState(null);
  
  // Test message
  const [testPhone, setTestPhone] = useState('');
  const [testContent, setTestContent] = useState('Test message from La Maisonette PMS - Connectivity Check');

  // Load data
  const loadData = useCallback(async () => {
    if (!token) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statusRes, healthRes, statsRes, messagesRes, eventsRes] = await Promise.all([
        axios.get(`${API}/admin/connectivity/status`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/connectivity/health`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/connectivity/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/connectivity/messages?limit=30`, { headers }).catch(() => ({ data: { messages: [] } })),
        axios.get(`${API}/admin/connectivity/events?limit=50`, { headers }).catch(() => ({ data: { events: [] } }))
      ]);
      
      setStatus(statusRes.data);
      setHealth(healthRes.data);
      setStats(statsRes.data);
      setMessages(messagesRes.data?.messages || []);
      setEvents(eventsRes.data?.events || []);
      setLastPing(new Date().toISOString());
      
    } catch (err) {
      console.error('Error loading connectivity data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
    // Auto-refresh ogni 30 secondi
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Refresh manuale
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Dati aggiornati');
  };

  // Verifica Health
  const handleVerifyHealth = async () => {
    setVerifyingHealth(true);
    try {
      const res = await axios.post(`${API}/admin/connectivity/verify-health`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.healthy) {
        toast.success(`✅ Sistema operativo - Latenza: ${res.data.latency_ms}ms`);
      } else {
        toast.error(`❌ Sistema non operativo: ${res.data.error}`);
      }
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore verifica health');
    } finally {
      setVerifyingHealth(false);
    }
  };

  // Send test message
  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error('Inserisci un numero di telefono');
      return;
    }
    
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/connectivity/test`, {
        to: testPhone,
        content: testContent
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success(`Messaggio inviato! ID: ${res.data.message_id}`);
        await loadData();
      } else {
        toast.error(res.data.error || 'Errore invio');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore invio');
    } finally {
      setTesting(false);
    }
  };

  // Test webhook
  const handleTestWebhook = async () => {
    setTestingWebhook(true);
    try {
      const res = await axios.post(`${API}/admin/connectivity/test-webhook`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        toast.success('Webhook test ricevuto correttamente');
        await loadData();
      } else {
        toast.error(res.data.error || 'Errore test webhook');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore test webhook');
    } finally {
      setTestingWebhook(false);
    }
  };

  // Status helpers
  const isConnectivityOnline = health?.transports?.connectivity?.healthy;
  const isLegacyOnline = health?.transports?.legacy?.healthy;
  const activeTransport = status?.active_transport || 'legacy_whatsapp';
  const connectivityLatency = health?.transports?.connectivity?.latency_ms;
  const apiVersion = health?.transports?.connectivity?.version || 'N/A';

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('it-IT');
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const diff = Date.now() - new Date(dateStr).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s fa`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m fa`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h fa`;
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <Wifi className="w-12 h-12 text-blue-500 mx-auto mb-3 animate-pulse" />
            <p className="text-gray-500">Caricamento diagnostica...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-6 space-y-6 bg-gray-50 min-h-screen" data-testid="connectivity-dashboard">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wifi className="w-7 h-7 text-blue-600" />
              Connectivity Dashboard
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              Monitoraggio completo Communication Service
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={handleVerifyHealth}
              disabled={verifyingHealth}
              className="gap-2"
            >
              {verifyingHealth ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              Verifica Health
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
          </div>
        </div>

        {/* Status Banner */}
        <div className={`rounded-2xl p-5 ${
          isConnectivityOnline ? 'bg-gradient-to-r from-emerald-50 to-emerald-100 border border-emerald-200' :
          isLegacyOnline ? 'bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200' :
          'bg-gradient-to-r from-rose-50 to-rose-100 border border-rose-200'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {isConnectivityOnline ? (
                <div className="w-14 h-14 bg-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
              ) : isLegacyOnline ? (
                <div className="w-14 h-14 bg-amber-500 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                  <AlertCircle className="w-8 h-8 text-white" />
                </div>
              ) : (
                <div className="w-14 h-14 bg-rose-500 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-500/30">
                  <XCircle className="w-8 h-8 text-white" />
                </div>
              )}
              <div>
                <h2 className={`font-bold text-xl ${
                  isConnectivityOnline ? 'text-emerald-700' :
                  isLegacyOnline ? 'text-amber-700' : 'text-rose-700'
                }`}>
                  {isConnectivityOnline ? '🟢 Connectivity Online' :
                   isLegacyOnline ? '🟠 Legacy Mode Attivo' : '🔴 Sistema Offline'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Transport attivo: <strong>{activeTransport}</strong>
                </p>
              </div>
            </div>
            
            {/* Quick stats */}
            <div className="hidden md:grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-800">{apiVersion}</p>
                <p className="text-xs text-gray-500">Versione API</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">
                  {connectivityLatency ? `${Math.round(connectivityLatency)}ms` : 'N/A'}
                </p>
                <p className="text-xs text-gray-500">Latenza</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{formatRelativeTime(lastPing)}</p>
                <p className="text-xs text-gray-500">Ultimo Ping</p>
              </div>
            </div>
          </div>
        </div>

        {/* Health Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Versione API */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Hash className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Versione API</p>
                  <p className="font-bold text-gray-800">{apiVersion}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tempo Risposta */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Timer className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Tempo Risposta</p>
                  <p className="font-bold text-gray-800">
                    {connectivityLatency ? `${Math.round(connectivityLatency)}ms` : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ultimo Ping */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ultimo Ping</p>
                  <p className="font-bold text-gray-800">{formatRelativeTime(lastPing)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ultimo Webhook */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Webhook className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ultimo Webhook</p>
                  <p className="font-bold text-gray-800 text-sm">
                    {stats?.last_webhook ? formatRelativeTime(stats.last_webhook.last_event_at) : 'Nessuno'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contatori Completi */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-indigo-500" />
              Contatori Messaggi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ArrowUp className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="text-3xl font-bold text-emerald-600">{stats?.status_counts?.sent || 0}</p>
                <p className="text-xs text-gray-500 font-medium">Sent</p>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Check className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-blue-600">{stats?.status_counts?.delivered || 0}</p>
                <p className="text-xs text-gray-500 font-medium">Delivered</p>
              </div>
              
              <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Eye className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-purple-600">{stats?.status_counts?.read || 0}</p>
                <p className="text-xs text-gray-500 font-medium">Read</p>
              </div>
              
              <div className="text-center p-4 bg-rose-50 rounded-xl border border-rose-100">
                <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <X className="w-5 h-5 text-rose-600" />
                </div>
                <p className="text-3xl font-bold text-rose-600">{stats?.status_counts?.failed || 0}</p>
                <p className="text-xs text-gray-500 font-medium">Failed</p>
              </div>
              
              <div className="text-center p-4 bg-cyan-50 rounded-xl border border-cyan-100">
                <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ArrowDown className="w-5 h-5 text-cyan-600" />
                </div>
                <p className="text-3xl font-bold text-cyan-600">{stats?.status_counts?.received || 0}</p>
                <p className="text-xs text-gray-500 font-medium">Received</p>
              </div>
              
              <div className="text-center p-4 bg-orange-50 rounded-xl border border-orange-100">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-3xl font-bold text-orange-600">{stats?.status_counts?.errored || 0}</p>
                <p className="text-xs text-gray-500 font-medium">Errored</p>
              </div>
            </div>

            {/* Last activity */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Ultimo Messaggio Inviato</p>
                {stats?.last_sent ? (
                  <div>
                    <p className="font-medium text-gray-800">{stats.last_sent.to}</p>
                    <p className="text-sm text-gray-500">{stats.last_sent.content?.substring(0, 50)}...</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTime(stats.last_sent.sent_at || stats.last_sent.created_at)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Nessun messaggio inviato</p>
                )}
              </div>
              
              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Ultimo Webhook Ricevuto</p>
                {stats?.last_webhook ? (
                  <div>
                    <p className="font-medium text-gray-800">{stats.last_webhook.last_event}</p>
                    <p className="text-sm text-gray-500">ID: {stats.last_webhook.message_id?.substring(0, 20)}...</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {formatTime(stats.last_webhook.last_event_at)}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Nessun webhook ricevuto</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transports Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Connectivity Transport */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-blue-500" />
                  Connectivity Transport
                </span>
                {isConnectivityOnline ? (
                  <Badge className="bg-emerald-100 text-emerald-700">Online</Badge>
                ) : (
                  <Badge className="bg-rose-100 text-rose-700">Offline</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Provider</span>
                  <span className="font-medium">{health?.transports?.connectivity?.provider || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Versione</span>
                  <span className="font-medium">{health?.transports?.connectivity?.version || 'N/A'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Latenza</span>
                  <span className="font-medium">
                    {health?.transports?.connectivity?.latency_ms 
                      ? `${Math.round(health.transports.connectivity.latency_ms)}ms` 
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-medium ${isConnectivityOnline ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {isConnectivityOnline ? 'Healthy' : 'Unhealthy'}
                  </span>
                </div>
                {health?.transports?.connectivity?.error && (
                  <div className="p-2 bg-rose-50 rounded-lg text-rose-700 text-xs">
                    {health.transports.connectivity.error}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Legacy Transport */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-amber-500" />
                  Legacy Transport (RCSoft)
                </span>
                {isLegacyOnline ? (
                  <Badge className="bg-emerald-100 text-emerald-700">Online</Badge>
                ) : (
                  <Badge className="bg-rose-100 text-rose-700">Offline</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Provider</span>
                  <span className="font-medium">{health?.transports?.legacy?.provider || 'RCSoft MYRC'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Environment</span>
                  <span className="font-medium">
                    {health?.transports?.legacy?.details?.environment || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Production</span>
                  <span className="font-medium">
                    {health?.transports?.legacy?.details?.is_production ? 'Sì' : 'No'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Status</span>
                  <span className={`font-medium ${isLegacyOnline ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {isLegacyOnline ? 'Healthy' : 'Unhealthy'}
                  </span>
                </div>
                {health?.transports?.legacy?.error && (
                  <div className="p-2 bg-rose-50 rounded-lg text-rose-700 text-xs">
                    {health.transports.legacy.error}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-500" />
              Configurazione
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">USE_CONNECTIVITY</p>
                <div className="flex items-center gap-2">
                  {status?.use_connectivity ? (
                    <>
                      <ToggleRight className="w-5 h-5 text-emerald-500" />
                      <Badge className="bg-emerald-100 text-emerald-700">true</Badge>
                    </>
                  ) : (
                    <>
                      <ToggleLeft className="w-5 h-5 text-gray-400" />
                      <Badge className="bg-gray-100 text-gray-600">false</Badge>
                    </>
                  )}
                </div>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Base URL</p>
                <p className="text-sm font-mono text-gray-700 truncate">
                  {status?.config?.base_url || 'Non configurato'}
                </p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Application ID</p>
                <p className="text-sm font-mono text-gray-700 truncate">
                  {status?.config?.application_id || 'Non configurato'}
                </p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Timeout</p>
                <p className="text-sm font-mono text-gray-700">
                  {status?.config?.timeout || 30}s
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Test Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500" />
              Azioni di Test
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Test Messaggio */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Invia Messaggio Test
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label>Numero Telefono</Label>
                    <Input 
                      placeholder="+393331234567"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Messaggio</Label>
                    <Input 
                      placeholder="Messaggio di test"
                      value={testContent}
                      onChange={(e) => setTestContent(e.target.value)}
                    />
                  </div>
                  <Button 
                    onClick={handleSendTest}
                    disabled={testing || !testPhone}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {testing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Invia Test ({activeTransport})
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Test Webhook */}
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <Webhook className="w-4 h-4" />
                  Test Webhook
                </h4>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-4">
                    Simula un webhook di delivery per verificare che il sistema riceva correttamente gli aggiornamenti di stato.
                  </p>
                  <Button 
                    onClick={handleTestWebhook}
                    disabled={testingWebhook}
                    variant="outline"
                    className="w-full"
                  >
                    {testingWebhook ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Test in corso...
                      </>
                    ) : (
                      <>
                        <Webhook className="w-4 h-4 mr-2" />
                        Test Webhook
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Log Eventi */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-gray-500" />
              Log Ultimi Eventi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {events.length === 0 && messages.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Nessun evento registrato</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {messages.map((msg, i) => (
                  <div key={i} className="p-3 bg-gray-50 rounded-lg flex items-start gap-3 hover:bg-gray-100 transition-colors">
                    <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                      msg.status === 'sent' ? 'bg-emerald-500' :
                      msg.status === 'delivered' ? 'bg-blue-500' :
                      msg.status === 'read' ? 'bg-purple-500' :
                      msg.status === 'failed' ? 'bg-rose-500' : 'bg-amber-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-medium text-sm text-gray-800 truncate">{msg.to}</p>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">
                          {msg.transport}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{msg.content?.substring(0, 60)}...</p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          msg.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                          msg.status === 'delivered' ? 'bg-blue-100 text-blue-700' :
                          msg.status === 'read' ? 'bg-purple-100 text-purple-700' :
                          msg.status === 'failed' ? 'bg-rose-100 text-rose-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {msg.status}
                        </span>
                        {msg.metadata?.business_event && (
                          <span className="text-[10px] text-gray-400">{msg.metadata.business_event}</span>
                        )}
                        <span className="text-[10px] text-gray-400">
                          {formatTime(msg.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </AdminLayout>
  );
}
